import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  CircleX,
  Mail,
  ChevronLeft,
  FileText,
  Calendar,
  ArrowDownUp,
  X,
  ChevronRight,
  Image,
} from "lucide-react";
import { GetSafetyTask, DoneSafetyTask } from "../../services/safetytask";
import { getStatusColor, formatDateDMY } from "../../utils";
import { TaskFormData } from "../../types/safetytask";
import { resolveImageUrl } from "../../utils/imageHelper";

const ITEMS_PER_PAGE = 2;

const SafetyTaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [base64Images, setBase64Images] = useState<string[]>([]);
  const [formData, setFormData] = useState<TaskFormData>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetchDetailSafetyTask = useCallback(
    async (
      safetytaskid: number,
      tenantid: number,
      safetystatusid: number,
      isoverdue: boolean
    ) => {
      try {
        const response = await GetSafetyTask(
          safetytaskid,
          tenantid,
          safetystatusid,
          isoverdue
        );
        if (response.Code === 0) {
          setFormData(response.safetyTaskDatas[0] || {});
        }
      } catch {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Error Fetching Data",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
    []
  );

  useEffect(() => {
    fetchDetailSafetyTask(parseInt(id || ""), 0, 0, false);
  }, [fetchDetailSafetyTask, id]);

  const totalPages = Math.ceil(
    (formData.safetyTaskHistoryDatas?.length ?? 0) / ITEMS_PER_PAGE
  );
  const paginatedHistory = formData.safetyTaskHistoryDatas?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleBack = () => {
    navigate("/SafetyTask");
  };

  const handleDone = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const safetyTaskImageDatas = base64Images.map((img) => ({
        DataImage: img,
        SafetyTaskId: Number(id),
      }));

      const response = await DoneSafetyTask(
        Number(id),
        notes,
        safetyTaskImageDatas
      );

      if (response.Code === 0) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Success Done Safety Task",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        setIsOpen(false);
        await fetchDetailSafetyTask(parseInt(id || ""), 0, 0, false);
        setBase64Images([]);
        setNotes("");
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error Done Safety Task",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    setNotes("");
  };

  const handleRemoveImage = (index: number) => {
    setBase64Images((prev) => {
      const updatedImages = [...prev];
      updatedImages.splice(index, 1);
      return updatedImages;
    });
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    Swal.fire({
      title: "Memproses Gambar...",
      text: "Mengoptimalkan ukuran gambar agar upload lebih cepat",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const compressionPromises = files.map((file) => {
        return new Promise<string>((resolve) => {
          if (!file.type.startsWith("image/")) {
            resolve("");
            return;
          }
          const reader = new FileReader();
          reader.onload = (event) => {
            const img = new window.Image();
            img.onload = () => {
              const canvas = document.createElement("canvas");
              let width = img.width;
              let height = img.height;

              const MAX_WIDTH = 1200;
              const MAX_HEIGHT = 1200;

              if (width > height) {
                if (width > MAX_WIDTH) {
                  height = Math.round((height * MAX_WIDTH) / width);
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width = Math.round((width * MAX_HEIGHT) / height);
                  height = MAX_HEIGHT;
                }
              }

              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext("2d");
              if (!ctx) {
                resolve(event.target?.result as string);
                return;
              }

              ctx.drawImage(img, 0, 0, width, height);
              // Compress to JPEG format with 0.7 quality factor
              const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
              resolve(dataUrl);
            };
            img.onerror = () => {
              resolve(event.target?.result as string);
            };
            img.src = event.target?.result as string;
          };
          reader.onerror = () => {
            resolve("");
          };
          reader.readAsDataURL(file);
        });
      });

      const results = await Promise.all(compressionPromises);
      const base64Array = results
        .filter((r) => r && r.includes(","))
        .map((r) => r.split(",")[1]);

      setBase64Images((prev) => [...prev, ...base64Array]);
      Swal.close();
    } catch (error) {
      console.error("Error compressing files", error);
      Swal.fire({
        icon: "error",
        title: "Gagal memproses gambar",
        text: "Silakan coba lagi.",
        timer: 2000,
        showConfirmButton: false,
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-8">

          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 sm:mb-8 font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={formData.SafetyCategoryName}
                  readOnly
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Due Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={formatDateDMY(formData.DueDate)}
                  readOnly
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-4 top-4 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <textarea
                  value={formData.Description}
                  readOnly
                  rows={3}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium ${getStatusColor(
                  formData.SafetyStatusName ? formData.SafetyStatusName : ""
                )}`}
              >
                <span className="w-2 h-2 rounded-full bg-current"></span>
                {formData.SafetyStatusName}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  value={formData.EmailNotification}
                  readOnly
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Safety Team
              </label>
              <div className="flex flex-wrap gap-3">
                {formData.safetyTaskImageDatas
                  ?.filter((img) => img.ImageTypeName === "Before")
                  .map((img, idx) => (
                    <div key={idx} className="relative group cursor-pointer" onClick={() => setZoomSrc(resolveImageUrl(img.FileImage))}>
                      <img
                        src={resolveImageUrl(img.FileImage)}
                        alt={`Before ${idx + 1}`}
                        className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:opacity-80 transition"
                      />
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Tenant
              </label>
              <div className="flex flex-wrap gap-3">
                {formData.safetyTaskImageDatas
                  ?.filter((img) => img.ImageTypeName === "After")
                  .map((img, idx) => (
                    <div key={idx} className="relative group cursor-pointer" onClick={() => setZoomSrc(resolveImageUrl(img.FileImage))}>
                      <img
                        src={resolveImageUrl(img.FileImage)}
                        alt={`After ${idx + 1}`}
                        className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:opacity-80 transition"
                      />
                    </div>
                  ))}

                {base64Images.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={`data:image/*;base64,${img}`}
                      alt={`Preview ${idx}`}
                      className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-xl shadow cursor-pointer hover:opacity-80 transition"
                      onClick={() => setZoomSrc(`data:image/*;base64,${img}`)}
                    />
                    <button
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-0 right-0 bg-red-200 w-6 h-6 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-300"
                    >
                      <CircleX className="w-6 h-6 text-red-500" />
                    </button>
                  </div>
                ))}

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />

                <button
                  onClick={handleImageClick}
                  className="w-24 h-24 sm:w-32 sm:h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition group"
                >
                  <Image className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500 group-hover:text-blue-500" />
                </button>
              </div>
            </div>
          </div>

          {formData.SafetyStatusName !== "Done" &&
            formData.SafetyStatusName !== "Close" &&
            formData.SafetyStatusName !== "Reject" && (
              <div className="flex justify-end mb-6 sm:mb-8">
                <button
                  onClick={() => setIsOpen(true)}
                  className="w-full sm:w-auto px-12 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition shadow-sm"
                >
                  Done
                </button>
              </div>
            )}

          <div>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
              History
            </h2>

            <div className="hidden sm:block border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          Created On
                          <ArrowDownUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          Safety Status Name
                          <ArrowDownUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          Notes
                          <ArrowDownUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedHistory?.map((entry, idx) => (
                      <tr key={idx} className="hover:bg-white dark:hover:bg-gray-700">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {formatDateDMY(entry.CreatedOn)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {entry.SafetyStatusName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {entry.Notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="sm:hidden space-y-3">
              {paginatedHistory?.length === 0 && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                  No history available
                </p>
              )}
              {paginatedHistory?.map((entry, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">Created On</span>
                    <span className="text-sm text-gray-900 dark:text-white font-medium">
                      {formatDateDMY(entry.CreatedOn)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">Status</span>
                    <span className="text-sm text-gray-900 dark:text-white">
                      {entry.SafetyStatusName}
                    </span>
                  </div>
                  {entry.Notes && (
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500 block mb-1">Notes</span>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {entry.Notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-6">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="sm:hidden text-sm text-gray-600 dark:text-gray-400 px-3">
                  {currentPage} / {totalPages}
                </span>

                <div className="hidden sm:flex items-center gap-1.5">
                  {(() => {
                    const pages: (number | string)[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      const left = Math.max(2, currentPage - 1);
                      const right = Math.min(totalPages - 1, currentPage + 1);
                      if (left > 2) pages.push("...");
                      for (let i = left; i <= right; i++) pages.push(i);
                      if (right < totalPages - 1) pages.push("...");
                      pages.push(totalPages);
                    }
                    return pages.map((p, idx) =>
                      typeof p === "number" ? (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`min-w-[40px] h-10 px-3 rounded-lg font-medium transition text-sm ${currentPage === p
                            ? "bg-blue-600 text-white"
                            : "border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                            }`}
                        >
                          {p}
                        </button>
                      ) : (
                        <span key={`dot-${idx}`} className="text-gray-400 px-1 select-none">...</span>
                      )
                    );
                  })()}
                </div>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm transition-all">
          <div className="relative w-full sm:max-w-md sm:mx-4 animate-[fadeIn_0.3s_ease]">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl border border-white/30 dark:border-gray-700/50 shadow-2xl rounded-t-2xl sm:rounded-2xl p-6">
              <div className="flex justify-center mb-4 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add Notes
                </h3>
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition"
                >
                  <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={notes}
                    disabled={isSubmitting}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Write your notes here..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none backdrop-blur-sm disabled:opacity-75"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/60 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDone}
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </>
                    ) : (
                      "Done"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {zoomSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setZoomSrc(null)}
        >
          <button
            onClick={() => setZoomSrc(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={zoomSrc}
            alt="Zoom"
            className="max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default SafetyTaskForm;